import React, { useEffect, useState } from 'react';
import { Paper, Button, styled } from '@mui/material';
import translation from 'i18n/translation.json';
import { CustomDialog } from 'components/CustomDialog';
import { Loader } from 'components/Loader';
import { ReactComponent as DownloadIcon } from 'assets/images/download.svg';
import { getContract, getContractPreview } from 'api';
import DOMPurify from 'dompurify';
import { isAndroid, isMobile } from 'react-device-detect';
import { ButtonContained } from 'components/Button';
import { CenteredText } from 'components/Common/styled-components';

interface IReceiptInfo {
  name: string;
  data: any;
}

interface PreviewContractDialogProps {
  url: string;
  staticDoc?: boolean;
  open: boolean;
  canDownLoad?: boolean;
  title?: string;
  subtitle?: string;
  isBlob?: boolean;
  contractLoading?: boolean;
  extraQueries?: object;
  onClose: () => void;
  onContinue?: () => void;
  onDownload?: (link: any) => void;
}

const styles = {
  '& .MuiDialog-paper': { height: '80vh' },
  '& .MuiPaper-root': {
    maxWidth: '500px',
    '@media (min-width: 500px)': {
      '& .static-wrapper::-webkit-scrollbar-thumb': {
        backgroundColor: '#939DB8',
        borderRadius: '10px',
        border: '5px solid #fff',
      },
      '& .static-wrapper::-webkit-scrollbar': {
        width: '15px',
      },
      '& .static-wrapper::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
    },
  },
};

const DocumentPaper = styled(Paper, {
  shouldForwardProp: prop => prop !== 'isStatic' && prop !== 'isBlob',
})<{ isStatic: boolean; isBlob: boolean }>(({ isStatic, isBlob }) => ({
  flex: 1,
  display: 'flex',
  boxShadow: isStatic ? 'unset' : 'initial',
  borderRadius: '10px',
  border: '1px solid #D4DFF9',
  justifyContent: isBlob ? 'center' : 'flex-start',
}));

const blobToBase64 = async (blob: Blob) => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const PreviewContractDialog = ({
  url,
  open,
  staticDoc,
  canDownLoad,
  title,
  subtitle,
  isBlob = false,
  contractLoading,
  extraQueries,
  onClose,
  onContinue,
  onDownload,
}: PreviewContractDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [document, setDocument] = useState('');
  const [downloadLink, setDownloadLink] = useState<any>(null);

  useEffect(() => {
    const getHtmlPreview = async () => {
      try {
        const response = await fetch(url);
        const text = await response.text();
        const sanitizedPreview = DOMPurify.sanitize(text, {
          WHOLE_DOCUMENT: true,
        });
        setDocument(sanitizedPreview);
      } catch {}
    };

    const getContractFC = async () => {
      setLoading(true);

      try {
        const trimmedUrl = url?.substring(4);
        const isExtension = url.includes('extension');

        const queryParams = new URLSearchParams({ is_mobile: isMobile ? '1' : '0' });

        if (extraQueries) {
          Object.entries(extraQueries).forEach(([key, value]) => {
            if (value != null) {
              queryParams.set(key, String(value));
            }
          });
        }

        const queryParam = queryParams.toString();
        const endpoint = `${trimmedUrl}${isExtension ? '&' : '?'}${queryParam}`;

        if (isBlob) {
          const contract = await getContract(endpoint);
          const blobReceipt = new Blob([contract.data], { type: 'application/pdf' });

          const receiptInfo: IReceiptInfo = { name: contract.headers['content-disposition'], data: '' };

          // NOTE: Distinguished implementation for android and other platforms because it was impossible to Open PDF file from preview
          if (isAndroid) {
            const base64Data: any = await blobToBase64(blobReceipt);
            receiptInfo.data = base64Data;
            setDocument(base64Data);
          } else {
            const pdfObjecttoURL = URL.createObjectURL(blobReceipt);
            receiptInfo.data = pdfObjecttoURL;

            setDocument(`${pdfObjecttoURL}#toolbar=0`);
          }

          setDownloadLink(receiptInfo);
        } else {
          const { data } = await getContractPreview(endpoint);

          const sanitizedContract = DOMPurify.sanitize(data, {
            WHOLE_DOCUMENT: true,
          });

          setDocument(sanitizedContract);
        }

        setLoading(false);
      } catch (error: any) {
        setError(translation.errors.serverError);
        setLoading(false);
      }
    };

    if (staticDoc) {
      getHtmlPreview();
    } else {
      getContractFC();
    }
  }, []);

  // TODO: I guess urls can be different and you need to expand this function functionality or rewrite it
  const getTitle = (titleProp: string) => {
    if (titleProp?.includes('terms_and_conditions')) {
      return translation.previewTermsAndConditions;
    }
    if (titleProp?.includes('privacy_policy')) {
      return translation.previewPrivacyPolicy;
    }

    return titleProp;
  };

  const handleDownload = () => {
    if (isBlob) {
      onDownload?.(downloadLink);
    } else {
      onDownload?.(null);
    }
  };

  return (
    <CustomDialog
      open={open}
      title={getTitle(title ?? url)}
      subtitle={subtitle}
      maxWidth="md"
      onClose={onClose}
      dialogStyles={styles}
      bodyStyles={{
        alignItems: error || loading ? 'center' : 'inherit',
        justifyContent: error || loading ? 'center' : 'inherit',
      }}
      bodyChildren={
        <>
          {loading ? (
            <Loader open={loading} isInDialog={true} />
          ) : error ? (
            <CenteredText color="#E02727">{error}</CenteredText>
          ) : (
            <DocumentPaper isStatic={!!staticDoc} isBlob={isBlob}>
              {isBlob ? (
                <object
                  data={document}
                  type="application/pdf"
                  style={{
                    width: isMobile ? 'auto' : '100%',
                    height: '100%',
                    display: 'flex',
                    border: 'none',
                    borderRadius: '10px',
                  }}>
                  {/* TODO: discuss whether to add an error text if PDF preview is not supported */}
                </object>
              ) : staticDoc ? (
                <div
                  className="static-wrapper"
                  dangerouslySetInnerHTML={{ __html: document }}
                  style={{
                    padding: `15px ${isMobile ? '15px' : 0} 0 15px`,
                    overflow: 'scroll',
                  }}></div>
              ) : (
                <iframe
                  title="Doc preview"
                  srcDoc={document}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', borderRadius: '10px' }}
                />
              )}
            </DocumentPaper>
          )}
        </>
      }
      buttonsChildren={
        canDownLoad ? (
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            disabled={!!error}
            loading={contractLoading}>
            {translation.downloadPDF}
          </Button>
        ) : (
          <ButtonContained size="medium" onClick={onContinue} />
        )
      }
    />
  );
};
